import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { ExportDataButton } from "components/ExportDataButton";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  DocumentType,
  Permission,
  PureOrMixture,
  TenantCatalogChemicalFragment,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemicalsBreadcrumb, getChemicalsCatalogBreadcrumb } from "util/breadcrumb";
import { formatChemicalName } from "util/chemicalName";
import { isAuthorizationError } from "utils/errorUtils";

import { AddChemicalButton } from "./AddChemicalButton";
import { ChemicalCatalogBulkActions } from "./ChemicalCatalogBulkActions";
import { buildChemicalRowActions } from "./ChemicalRowActions";
import { DELETE_CHEMICAL, TENANT_CATALOG_CHEMICALS } from "./schema";
import { StaffBulkActionsMenu } from "./StaffBulkActionsMenu";

type Row = TenantCatalogChemicalFragment;

function buildColumns({
  onOpen,
  onDelete,
}: {
  onOpen: (row: Row) => void;
  onDelete: (row: Row) => void;
}): OmnisearchGridColDef<Row>[] {
  return [
    {
      field: "name",
      headerName: "Chemical Name",
      flex: 0.5,
      renderCell: ({ row }) => (
        <TextCellWithTooltip tooltip={formatChemicalName(row)}>
          {formatChemicalName(row, {
            showCasNumber: false,
            showPureOrMixture: false,
            showMixtureDetails: false,
            showStateOfMatter: false,
            showAlternateId: false,
          })}
        </TextCellWithTooltip>
      ),
    },
    {
      field: "casNumber",
      headerName: "CAS Number",
      flex: 0.25,
      renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
    },
    {
      field: "alternateId",
      headerName: "Alternate ID",
      flex: 0.25,
      renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
    },
    {
      field: "stateOfMatter",
      headerName: "State of Matter",
      flex: 0.3,
      renderCell: ({ value }) => {
        if (!value || value.length === 0) return <></>;
        return (
          <TextCellWithTooltip>
            {value.map((state: string) => prettyPrintEnumValue(state)).join(", ")}
          </TextCellWithTooltip>
        );
      },
      filterKeyType: "stateOfMatter",
    },
    {
      field: "density",
      headerName: "Density",
      flex: 0.3,
      renderCell: ({ row }) => {
        const { density, densityUnits } = row;
        if (!density || !densityUnits) return <></>;
        return (
          <TextCellWithTooltip>
            {density} {prettyPrintEnumValue(densityUnits)}
          </TextCellWithTooltip>
        );
      },
    },
    {
      field: "EHS",
      headerName: "EHS",
      renderCell(params) {
        const isEhs = params.row.isEhs;
        if (!isEhs) return <></>;
        return <Check />;
      },
      filterKeyType: "boolean",
    },
    {
      field: "mixture",
      headerName: "Mixture",
      renderCell(params) {
        const pureOrMixture = params.row.pureOrMixture;
        if (pureOrMixture === PureOrMixture.Mixture) return <Check />;
        return <></>;
      },
      filterKeyType: "boolean",
    },
    {
      field: "SDS",
      headerName: "SDS",
      sortable: false,
      renderCell(params) {
        const hasSDS = params.row.documents
          ?.map((d) => d.document.documentType)
          .some((t) => t === DocumentType.SafetyDataSheet);
        return hasSDS ? <Check /> : <></>;
      },
      filterKeyType: "boolean",
    },
    {
      field: "needsReview",
      headerName: "Needs Review?",
      width: 110,
      renderCell: (params) => (params.row.needsReview ? <Check /> : <></>),
      filterKeyType: "boolean",
    },
    {
      field: "issueCount",
      headerName: "Issues",
      renderCell: ({ row: { issueCount } }) => <IssueCount issueCount={issueCount} />,
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      getActions: ({ row }) => buildChemicalRowActions({ row, onOpen, onDelete }),
    },
  ];
}

export const TenantChemicalsTable: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { tenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  const canAddChemical = hasPermissions([Permission.WriteChemicalCatalog]);
  const [selectedChemical, setSelectedChemical] = useState<Row | undefined>(undefined);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  useBreadcrumb([
    {
      label: "Chemicals",
      to: tenantId ? getChemicalsBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Chemical Catalog",
      to: tenantId ? getChemicalsCatalogBreadcrumb(tenantId) : undefined,
    },
  ]);

  const [deleteChemical, { loading: deleting }] = useMutation(DELETE_CHEMICAL);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteConfirmOpen(false);
    setSelectedChemical(undefined);
  }, []);

  const handleConfirmDeletion = useCallback(async () => {
    try {
      if (selectedChemical) {
        await deleteChemical({
          variables: {
            id: selectedChemical.id,
          },
          refetchQueries: ["TenantCatalogChemicals", "CatalogOverview"],
        });
        alerts.success("Successfully deleted chemical");
        handleCloseDeleteDialog();
      }
    } catch (err) {
      if (isAuthorizationError(err)) {
        alerts.error("You do not have permission to delete this chemical");
      } else {
        alerts.error("An error occurred while deleting the chemical", err);
      }
    }
  }, [alerts, deleteChemical, selectedChemical, handleCloseDeleteDialog]);

  const handleOpenDeleteDialog = useCallback((chemical?: Row) => {
    setSelectedChemical(chemical);
    setDeleteConfirmOpen(true);
  }, []);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () =>
      buildColumns({
        onOpen: (chemical) => navigate(`./${chemical.id}`),
        onDelete: handleOpenDeleteDialog,
      }),
    [handleOpenDeleteDialog, navigate]
  );

  const commandButtons = useMemo(() => {
    const buttons = [
      <ExportDataButton
        key="export"
        exportType="chemical"
        disabled={!tenantId}
        tenantId={tenantId ?? ""}
        fileName={tenant?.name ? `${tenant.name}-chemical-catalog` : "chemical-catalog"}
      />,
      ...(canAddChemical ? [<AddChemicalButton key="add" />] : []),
      <ChemicalCatalogBulkActions key="bulk-actions" chemicals={selectedRows} />,
    ];
    if (isStaff) {
      buttons.push(<StaffBulkActionsMenu key="staff" />);
    }
    return buttons;
  }, [isStaff, canAddChemical, tenantId, selectedRows, tenant]);

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="chemical-catalog"
        isURLDriven
        additionalFilterColumns={[{ header: "Manufacturer/Supplier", key: "manufacturerSupplier" }]}
        excludeFilterColumns={["issueCount"]}
        columns={columns}
        initialSortModel={[
          { field: "needsReview", sort: "desc" },
          { field: "name", sort: "asc" },
        ]}
        initialState={{
          columns: {
            columnVisibilityModel: {
              stateOfMatter: false,
              density: false,
            },
          },
        }}
        dataQuery={TENANT_CATALOG_CHEMICALS}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.chemicals.items}
        getCount={(data) => data.chemicals.count}
        noDataOnButtonClick={canAddChemical ? () => navigate("new") : undefined}
        noDataMessage="No chemicals have been added to the catalog yet."
        noDataButtonText={canAddChemical ? "Add Chemical" : undefined}
        onSelectedRowsChanged={setSelectedRows}
        // Every bulk action writes the chemical.
        isRowSelectable={({ row }) => row.canWrite}
        // Greys out the select-all checkbox when no row can be selected.
        disableMultipleRowSelection={!canAddChemical}
        commandButtons={commandButtons}
        getRowLink={(row) => `./${row.id}`}
        onRowClick={(params) => navigate(`./${params.row.id}`)}
        columnVisibilityModel={{
          stateOfMatter: false,
          density: false,
          ...(isMobile && {
            alternateId: false,
            EHS: false,
            mixture: false,
            SDS: false,
            needsReview: false,
            issueCount: false,
          }),
        }}
      />
      <ConfirmDialog
        loading={deleting}
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeletion}
        header="Are you sure you want to delete this chemical?"
        resources={[
          {
            resourceType: "Chemical",
            resourceName: selectedChemical?.name,
          },
        ]}
        info="This will also delete all associated inventory."
      />
    </Box>
  );
};
